import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import toast, { Toaster } from 'react-hot-toast';
import { registerApi } from '../../api/authApi';
import { motion } from 'framer-motion';
import { SparklesIcon } from '@heroicons/react/24/solid';

const Register = () => {
  const [formData, setFormData] = useState({
    name: 'Test',
    email: 'test@gmail.com',
    phone: '9876543210',
    password: 'Test@123',
    businessName: 'Test@123'
  });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await registerApi(formData);
      toast.success('Registration successful! Please log in.');
      navigate('/login');
    } catch (err) {
      const errorMsg = err.response?.data?.message || err.message || 'Registration failed';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-white font-sans">
      <Toaster position="top-right" />
      
      {/* Left Panel: Graphic & Branding */}
      <div className="hidden lg:flex lg:w-1/2 relative bg-[#dc343b] overflow-hidden flex-col justify-between p-12 lg:p-20">
        
        {/* Cool Abstract Background */}
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20 brightness-100 contrast-150 mix-blend-overlay"></div>
          <div className="absolute top-0 left-0 -translate-y-1/4 -translate-x-1/4 w-[800px] h-[800px] bg-gradient-to-br from-[#f43f5e] to-[#ec4899] rounded-full blur-[100px] opacity-60"></div>
        </div>

        {/* Branding */}
        <div className="relative z-10">
          <Link to="/" className="inline-flex items-center gap-3">
            <div className="flex items-center justify-center bg-white text-[#dc343b] p-2 rounded-xl shadow-lg">
              <svg xmlns='http://www.w3.org/2000/svg' width='28' height='28' viewBox='0 0 24 24' fill='currentColor'>
                <path d='M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z'/>
              </svg>
            </div>
            <span className="text-2xl font-extrabold tracking-tight text-white uppercase">
              NETROOTX BILLING
            </span>
          </Link>
        </div>

        {/* Hero Text */}
        <div className="relative z-10 mt-auto mb-20">
          <h1 className="text-4xl font-extrabold tracking-tight text-white sm:text-5xl lg:text-[56px] leading-[1.1] mb-8">
            Start growing <br/> your enterprise today.
          </h1>
          <p className="text-lg leading-relaxed text-red-100 max-w-md">
            Join over 1 Million MSMEs who trust Khatabook to manage their ledgers, invoicing, and inventory securely from anywhere in the world.
          </p>
        </div>
        
        {/* Decorative elements */}
        <div className="relative z-10 flex items-center gap-4 bg-white/10 backdrop-blur-md p-6 rounded-3xl border border-white/20 w-fit">
          <div className="flex -space-x-3">
            <img className="w-10 h-10 rounded-full ring-2 ring-[#dc343b] object-cover" src="https://i.pravatar.cc/150?img=31" alt="User" />
            <img className="w-10 h-10 rounded-full ring-2 ring-[#dc343b] object-cover" src="https://i.pravatar.cc/150?img=32" alt="User" />
            <img className="w-10 h-10 rounded-full ring-2 ring-[#dc343b] object-cover" src="https://i.pravatar.cc/150?img=33" alt="User" />
          </div>
          <div>
            <div className="flex text-yellow-400 text-sm">★★★★★</div>
            <p className="text-[11px] font-bold text-white uppercase tracking-widest mt-1">TRUSTED BY THOUSANDS</p>
          </div>
        </div>

      </div>

      {/* Right Panel: Form */}
      <div className="flex flex-1 flex-col justify-center px-4 py-8 sm:px-6 lg:flex-none lg:w-1/2 lg:px-20 xl:px-32 relative">
        
        {/* Mobile Logo (hidden on desktop) */}
        <div className="absolute top-6 left-6 lg:hidden">
          <Link to="/" className="inline-flex items-center gap-2">
            <div className="flex items-center justify-center bg-[#dc343b] text-white p-1.5 rounded-lg shadow-md">
              <svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='currentColor'>
                <path d='M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z'/>
              </svg>
            </div>
            <span className="text-lg font-extrabold tracking-tight text-[#0f172a] uppercase">Khatabook</span>
          </Link>
        </div>

        <motion.div 
          initial={{ opacity: 0, x: 30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="mx-auto w-full max-w-md"
        >
          <div className="mb-6">
            <h2 className="text-2xl font-extrabold tracking-tight text-gray-900 mb-1 flex items-center gap-2">
              Create an account <SparklesIcon className="w-5 h-5 text-[#dc343b]" />
            </h2>
            <p className="text-[14px] text-gray-500 font-medium">
              Join Khatabook to manage your business efficiently.
            </p>
          </div>

          <form className="space-y-4" onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              
              <div className="sm:col-span-2">
                <label className="block text-[11px] font-bold tracking-wider text-gray-900 uppercase mb-1">Full Name</label>
                <div className="relative">
                  <input
                    name="name"
                    type="text"
                    required
                    className="block w-full rounded-xl border border-gray-200 px-4 py-2.5 text-gray-900 shadow-sm placeholder:text-gray-400 focus:border-[#dc343b] focus:ring-1 focus:ring-[#dc343b] sm:text-[14px] bg-white transition-colors"
                    placeholder="e.g. John Doe"
                    value={formData.name}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div className="sm:col-span-2">
                <label className="block text-[11px] font-bold tracking-wider text-gray-900 uppercase mb-1">Business Name</label>
                <div className="relative">
                  <input
                    name="businessName"
                    type="text"
                    required
                    className="block w-full rounded-xl border border-gray-200 px-4 py-2.5 text-gray-900 shadow-sm placeholder:text-gray-400 focus:border-[#dc343b] focus:ring-1 focus:ring-[#dc343b] sm:text-[14px] bg-white transition-colors"
                    placeholder="e.g. Acme Corporation"
                    value={formData.businessName}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div>
                <label className="block text-[11px] font-bold tracking-wider text-gray-900 uppercase mb-1">Email address</label>
                <div className="relative">
                  <input
                    name="email"
                    type="email"
                    required
                    className="block w-full rounded-xl border border-gray-200 px-4 py-2.5 text-gray-900 shadow-sm placeholder:text-gray-400 focus:border-[#dc343b] focus:ring-1 focus:ring-[#dc343b] sm:text-[14px] bg-white transition-colors"
                    placeholder="john@example.com"
                    value={formData.email}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div>
                <label className="block text-[11px] font-bold tracking-wider text-gray-900 uppercase mb-1">Phone Number</label>
                <div className="relative">
                  <input
                    name="phone"
                    type="tel"
                    required
                    className="block w-full rounded-xl border border-gray-200 px-4 py-2.5 text-gray-900 shadow-sm placeholder:text-gray-400 focus:border-[#dc343b] focus:ring-1 focus:ring-[#dc343b] sm:text-[14px] bg-white transition-colors"
                    placeholder="+91 9876543210"
                    value={formData.phone}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div className="sm:col-span-2">
                <label className="block text-[11px] font-bold tracking-wider text-gray-900 uppercase mb-1">Password</label>
                <div className="relative">
                  <input
                    name="password"
                    type="password"
                    required
                    className="block w-full rounded-xl border border-gray-200 px-4 py-2.5 text-gray-900 shadow-sm placeholder:text-gray-400 focus:border-[#dc343b] focus:ring-1 focus:ring-[#dc343b] sm:text-[14px] bg-white transition-colors"
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={handleChange}
                  />
                </div>
              </div>
            </div>

            <div className="pt-2">
              <button
                type="submit"
                disabled={loading}
                className="flex w-full justify-center items-center rounded-xl bg-[#0f172a] px-4 py-3.5 text-[15px] font-bold text-white shadow-lg shadow-slate-900/20 hover:bg-slate-800 hover:-translate-y-0.5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 transition-all disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                ) : 'Create free account'}
              </button>
            </div>
          </form>

          <div className="mt-6 text-center text-[14px] text-gray-500">
            Already have an account?{' '}
            <Link to="/login" className="font-semibold text-[#dc343b] hover:text-[#b91c1c]">
              Sign in to Dashboard
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Register;
